import React, { createContext, ReactChild, useContext, useEffect, useState } from "react";
import {
  getPricingContextAsQueryString,
  getPricingContextEncodedString,
  initializePricingContext,
} from "../utils/pricingContext";
import PageConfig from "../utils/PageConfig";

// @ts-ignore
import pricingContextModuleHelpers from "@vp/pricing-context-module/helpers";
import { IdentityContext } from "./IdentityProvider";
import PcxtCookieProvider from "../utils/pcxtCookieProvider";
import { PageStaticDataContext } from "./PageStaticDataContextProvider";

interface PricingContextProps {
  defaultVatIncl: boolean;
  children?: ReactChild | ReactChild[] | undefined | null;
}

interface PricingContextType {
  vatInclusivity: boolean;
  isPricingContextInitialized: boolean;
  encodedPricingContext: string;
  queryStringEncodedPricingContext: string;
}

export const PricingContext = createContext<PricingContextType>({
  vatInclusivity: false,
  isPricingContextInitialized: false,
  encodedPricingContext: "",
  queryStringEncodedPricingContext: "",
});

export const PricingContextProvider = (props: PricingContextProps) => {
  const { defaultVatIncl, children } = props;
  const { country } = useContext(PageStaticDataContext);

  const { isIdentityInitialized } = useContext(IdentityContext);

  const [pricingState, setPricingState] = useState({
    isPricingContextInitialized: false,
    vatInclusivity: defaultVatIncl,
    encodedPricingContext: "",
    queryStringEncodedPricingContext: "",
  });

  useEffect(() => {
    let mounted = true;

    const updateVat = () => {
      const vat = PcxtCookieProvider.getVat(defaultVatIncl);
      if (mounted) {
        setPricingState({
          ...pricingState,
          vatInclusivity: vat,
          encodedPricingContext: getPricingContextEncodedString(),
          queryStringEncodedPricingContext: getPricingContextAsQueryString(),
        });
      }
    };

    const initContext = async () => {
      if (isIdentityInitialized && !pricingState.isPricingContextInitialized) {
        const developmentMode = PageConfig.isDevelopment;
        const tenant = PageConfig.tenant;

        document.addEventListener(
          pricingContextModuleHelpers.EVENTS.VAT_INCLUSIVITY_CHANGE_EVENT,
          updateVat,
          false
        );

        await initializePricingContext(tenant, country, developmentMode);
        if (mounted) {
          const vat = PcxtCookieProvider.getVat(defaultVatIncl);
          setPricingState({
            isPricingContextInitialized: true,
            vatInclusivity: vat,
            encodedPricingContext: getPricingContextEncodedString(),
            queryStringEncodedPricingContext: getPricingContextAsQueryString(),
          });
        }
      }
    };
    initContext();

    return () => {
      mounted = false;
      document.removeEventListener(pricingContextModuleHelpers.EVENTS.INITIALIZED_EVENT, updateVat);
      document.removeEventListener(
        pricingContextModuleHelpers.EVENTS.VAT_INCLUSIVITY_CHANGE_EVENT,
        updateVat
      );
    };
  }, [isIdentityInitialized]);

  return <PricingContext.Provider value={{ ...pricingState }}>{children}</PricingContext.Provider>;
};
